'use client';

import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchAPI, SearchResult } from '@/lib/api';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { getImageUrl } from '@/lib/utils';

export default function SearchBox() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // 搜索查询
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search', searchQuery],
    queryFn: () => searchAPI.search({ q: searchQuery, limit: 10 }),
    enabled: searchQuery.trim().length >= 2, // 至少2个字符才开始搜索
    staleTime: 30000, // 30秒缓存
  });

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsOpen(value.trim().length >= 2);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setIsOpen(false);
    }
  };

  const handleResultClick = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      product: '产品',
      post: '新闻',
      case: '案例',
      download: '下载',
      video: '视频',
    };
    return typeMap[type] || type;
  };

  const results = searchResults?.data || [];

  return (
    <div className="ast-builder-layout-element ast-flex site-header-focus-item ast-header-search" data-section="section-header-search" ref={searchRef}>
      <div className="ast-search-menu-icon search-box ast-inline-search" style={{ position: 'relative' }}>
        <form role="search" method="get" className="search-form" onSubmit={handleSubmit}>
          <label>
            <span className="screen-reader-text">Search for:</span>
            <input
              type="search"
              className="search-field"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={() => {
                if (searchQuery.trim().length >= 2) {
                  setIsOpen(true);
                }
              }}
              name="s"
            />
          </label>
          <button type="submit" className="search-submit normal-search" value="Search" aria-label="Search">
            <i className="astra-search-icon">
              <span className="ast-icon icon-search icon-search">
                <span className="ahfb-svg-iconset ast-inline-flex svg-baseline">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"></path>
                  </svg>
                </span>
              </span>
            </i>
          </button>
        </form>

        {/* 搜索结果下拉框 */}
        {isOpen && searchQuery.trim().length >= 2 && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 1000,
              marginTop: '4px',
            }}
          >
            {isLoading ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                搜索中...
              </div>
            ) : results.length > 0 ? (
              <>
                {results.map((result: SearchResult) => (
                  <Link
                    key={`${result.type}-${result.id}`}
                    href={result.url}
                    onClick={handleResultClick}
                    style={{
                      display: 'block',
                      padding: '12px 16px',
                      borderBottom: '1px solid #f0f0f0',
                      textDecoration: 'none',
                      color: '#333',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      {result.featuredImage || result.thumbnail ? (
                        <img
                          src={getImageUrl(result.featuredImage || result.thumbnail)}
                          alt={result.title}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span
                            style={{
                              fontSize: '12px',
                              padding: '2px 6px',
                              backgroundColor: '#f0f0f0',
                              borderRadius: '2px',
                              color: '#666',
                            }}
                          >
                            {getTypeLabel(result.type)}
                          </span>
                          <h4
                            style={{
                              margin: 0,
                              fontSize: '14px',
                              fontWeight: 600,
                              color: '#333',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {result.title}
                          </h4>
                        </div>
                        {(result.description || result.excerpt) && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: '12px',
                              color: '#666',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {result.description || result.excerpt}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
                <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
                  <Link
                    href={`/search?q=${encodeURIComponent(searchQuery)}`}
                    onClick={handleResultClick}
                    style={{
                      color: '#1890ff',
                      textDecoration: 'none',
                      fontSize: '14px',
                    }}
                  >
                    查看全部结果 ({searchResults?.total || 0})
                  </Link>
                </div>
              </>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                未找到相关结果
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

